import React from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  Loader,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import RequestDetails from '../../../shared/components/AuditLogViewer/RequestDetails';
import { CF_CRT_MED, CF_NEU_BLD, CF_SAV_MED } from '../../../shared/constants';
import { useInvertedLocalize, useTypedSelector } from '../../../shared/hooks';
import useScrollableContent from '../../../shared/hooks/use-scrollablecontent';
import { type InsightsPanelProps, type TCodeData } from '../../../shared/types';
import { useLazyGetInsightsQuery } from '../data-layer';
import { auditLogActions } from '../data-store';
import { useApiCancellation } from '../hooks/useApiCancellation';

import InsightsDetails from './InsightsDetails';
import InsightsList from './InsightsList';
import useInsightsState from './useInsightsState';

import './InsightsPanel.css';

/**
 * InsightsPanel Component
 * Left sidebar panel for displaying audit log insights.
 * This component provides the layout structure for the insights panel.
 * The actual insight content will be added as child components.
 */
function InsightsPanel({
  className,
  onClose,
}: InsightsPanelProps): React.ReactElement {
  const { selectedTCode, handleTCodeClick, handleBackToInsights } =
    useInsightsState();

  const { isScrollable, contentRef } = useScrollableContent();
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  // Request access key from Redux metadata
  const requestAccessKey = useTypedSelector(
    (s) =>
      (
        s.aag.auditLog as {
          requestMetadata?: { requestAccessKey?: number | string };
        }
      ).requestMetadata?.requestAccessKey
  );

  // Lazy fetch insights on panel open, cache until close
  const [triggerInsights, insightsResult] = useLazyGetInsightsQuery();
  const { data: insightsData, isFetching, isError } = insightsResult;
  const { registerAbort, cancelRequest } = useApiCancellation();

  const listData: TCodeData[] = React.useMemo(() => {
    const rawItems = insightsData?.items;
    const arr = Array.isArray(rawItems)
      ? (rawItems as {
          id: string;
          description: string;
          params?: Record<string, string | number>;
          severity?: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE';
        }[])
      : [];

    return arr.map((i) => ({
      id: i.id,
      description: i.description,
      params: i.params,
      severity: i.severity,
    }));
  }, [insightsData]);

  React.useEffect(() => {
    if (requestAccessKey) {
      // Cancel previous insights request if any
      cancelRequest('getInsights');

      const promise = triggerInsights({ requestAccessKey });

      // Register abort function for this request
      const abortablePromise = registerAbort(
        promise as unknown as ReturnType<typeof triggerInsights> & {
          abort?: () => void;
        },
        'getInsights'
      );

      abortablePromise
        .unwrap()
        .then(() => undefined)
        .catch(() => undefined);
    }
  }, [requestAccessKey, triggerInsights, registerAbort, cancelRequest]);

  // Save insights to Redux when fetched
  React.useEffect(() => {
    try {
      if (listData.length > 0) {
        dispatch(auditLogActions.setInsights(listData));
      } else if (!isFetching && !isError) {
        dispatch(auditLogActions.clearInsights());
      }
    } catch {
      // no-op
    }
  }, [listData, isFetching, isError, dispatch]);

  const selectedEntitlements = React.useMemo(() => {
    const rawItems = insightsData?.items;
    const arr = Array.isArray(rawItems)
      ? (rawItems as {
          id: string;
          entitlements?: {
            entitlementDisplayName: string;
            entitlementDescription?: string | null;
            entitlementUsage?: number | string;
            entitlementDeviation?: boolean;
          }[];
        }[])
      : [];

    const selected = arr.find((i) => i.id === selectedTCode);

    return selected?.entitlements ?? [];
  }, [insightsData, selectedTCode]);

  return (
    <Box className={cx('Sod-InsightsPanel', className)}>
      {/* Header Section */}
      <Box className='Sod-InsightsPanel-header'>
        <Box className='Sod-InsightsPanel-header-front'>
          <Icon kind='SaviCompanion' color={CF_SAV_MED} size='large' />
          <Typography
            lineLimit={3}
            kind='h2'
            className='Sod-InsightsPanel-header-front-text'>
            {l('Findings')}
          </Typography>
        </Box>
        <Box className='Sod-InsightsPanel-header-icon'>
          <ButtonIcon
            size='medium'
            kind='ghost'
            ariaLabel={l('Close insights panel')}
            icon={<Icon kind='Close' />}
            onClick={() => {
              if (onClose) {
                onClose();
              }
            }}
          />
        </Box>
      </Box>

      {/* Content Section - Dynamic Height */}
      <div
        ref={contentRef}
        className={cx('Sod-InsightsPanel-content', {
          scrollable: isScrollable,
        })}>
        {/* Insights Content - Conditional Rendering */}
        {selectedTCode ? (
          <InsightsDetails
            selectedTCode={selectedTCode}
            tCodeData={listData}
            entitlements={selectedEntitlements}
            onBack={handleBackToInsights}
          />
        ) : (
          <>
            {/* T-Codes Section */}
            <Box className='Sod-InsightsPanel-tcodes-section'>
              {/* Standard loading/error states */}
              {isFetching && (
                <Box className='Sod-InsightsPanel-placeholder'>
                  <Loader kind='dots' format='inline' color='brand' />
                </Box>
              )}
              {!isFetching && isError && (
                <Box className='Aag-TimeLineBlock-empty'>
                  <Box className='Aag-TimeLineBlock-emptyContent'>
                    <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
                    <Typography kind='body2' color={CF_CRT_MED}>
                      {l("Data couldn't be retrieved.")}
                    </Typography>
                    <Box style={{ marginTop: '0.75rem' }}>
                      <Button
                        kind='filled'
                        size='small'
                        onClick={() => {
                          if (requestAccessKey) {
                            // Cancel previous insights request if any
                            cancelRequest('getInsights');

                            const promise = triggerInsights({
                              requestAccessKey,
                            });

                            // Register abort function for this request
                            const abortablePromise = registerAbort(
                              promise as unknown as ReturnType<
                                typeof triggerInsights
                              > & {
                                abort?: () => void;
                              },
                              'getInsights'
                            );

                            abortablePromise
                              .unwrap()
                              .then(() => undefined)
                              .catch(() => undefined);
                          }
                        }}>
                        {l('Refresh')}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
              {!isFetching && !isError && listData.length === 0 && (
                <Box className='Sod-InsightsPanel-placeholder'>
                  <Box tag='div' className='SodInsights-loadingWrapper'>
                    <Box
                      tag='div'
                      className='SodInsights-loadingWrapper-message'>
                      <Icon kind='AlertInfo' size='large' color={CF_NEU_BLD} />
                      <Typography kind='body2'>
                        {l('No Findings Available')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              {!isFetching && !isError && listData.length > 0 && (
                <InsightsList
                  tCodeData={listData}
                  onCodeClick={handleTCodeClick}
                  riskScore={insightsData?.riskScore}
                />
              )}
            </Box>

            {/* Session Data Section - Right below T-codes */}
            <Box className='Sod-InsightsPanel-sessions'>
              <Box className='Sod-InsightsPanel-sessions-header'>
                <Typography lineLimit={3} kind='h3'>
                  {l('Request Details')}
                </Typography>
              </Box>

              {/* Session Details (mirror header popover) */}
              <Box className='Sod-InsightsPanel-sessions-details'>
                <RequestDetails />
              </Box>
            </Box>
          </>
        )}
      </div>
    </Box>
  );
}

InsightsPanel.defaultProps = {
  className: undefined,
  onClose: undefined,
};

export default InsightsPanel;
